import React from "react";

import { createRoot, hydrateRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";

import { getThemeStyles } from "@atlaskit/tokens";
import { MeResponse } from "@atlassiansox/asc-stargate";

import {
    IntercomProperties,
    IntercomSettingsProperties
} from "@/libraries/intercom/src";
import { ConfirmationPageOverlayVariants } from "@/templates/confirmation-page/src/hooks";

import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import { CrossFlowProvider } from "./components/cross-flow";
import {
    TemplateCollection,
    type Template
} from "./content/templates/template.types";
import { type AppPricingData } from "./contentful";
import { initializeFeatureGatesClient } from "./feature-gates/initialization";
import { Layout } from "./layout";
import {
    AvailableProductsResponse,
    GetSignupUserTraitsResponse
} from "./libraries/bxp-web-stargate-client/src";
import { PageWrapper } from "./page-wrapper";
import { Tutorial } from "./templates/agile-hub/tutorial-types";
import { AgileArticle, AgileOverview } from "./templates/agile-hub/types";
import { CustomersLanding } from "./templates/customers-landing-page/utils";
import { AnchorContentsResponse } from "./utils/anchor-queries/get-anchor-contents";
import { GridOverlayFunction } from "./utils/grid-overlay";
import { SentryIntlProvider } from "./utils/sentry-intl";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

window.BUILD_KEY = process.env["VERSION"];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

declare global {
    type FeatureFlagType =
        | "boolean"
        | "string"
        | "number"
        | ConfirmationPageOverlayVariants;

    interface Window {
        IS_STATIC?: boolean;
        BUILD_KEY?: string;
        SSR_languagePack: Record<string, string>;
        prices?: AppPricingData;
        availableProducts?: AvailableProductsResponse;
        signupUserTraits?: GetSignupUserTraitsResponse;
        userProfile?: MeResponse;
        agileArticle?: AgileArticle;
        agileOverview?: AgileOverview;
        tutorial?: Tutorial;
        customersLanding?: CustomersLanding;
        templates?: Template[];
        partnerTemplates?: Template[];
        collections?: TemplateCollection[];
        categories?: TemplateCategory[];
        themeStyles: Awaited<ReturnType<typeof getThemeStyles>>;
        anchorContents?: AnchorContentsResponse;
        persona?: string;
        teamType?: string;
        featureFlags: Record<string, FeatureFlagType>;
        countryCode?: UserLocale;
        Intercom?: IntercomProperties;
        intercomSettings?: IntercomSettingsProperties;
        toggleGrid?: GridOverlayFunction;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const init = (Component: React.ComponentType<any>) => {
    const isStatic = typeof window !== "undefined" && window.IS_STATIC;
    const { locale } = parseRoute(window.location);

    initializeFeatureGatesClient(window.featureGateBootstrapValues).then(() => {
        if (isStatic) {
            // @ts-ignore
            const root = createRoot(document.getElementById("wac"));
            root.render(
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                >
                    <CrossFlowProvider locale={locale}>
                        <PageWrapper
                            pureClientMode={true}
                            location={window.location}
                            pricing={window.prices}
                            availableProducts={window.availableProducts}
                            signupUserTraits={window.signupUserTraits}
                            userProfile={window.userProfile}
                            countryCode={window.countryCode}
                            agileArticle={window.agileArticle}
                            agileOverview={window.agileOverview}
                            tutorial={window.tutorial}
                            customersLanding={window.customersLanding}
                            templates={window.templates}
                            partnerTemplates={window.partnerTemplates}
                            collections={window.collections}
                            categories={window.categories}
                            anchorContents={window.anchorContents}
                            persona={window.persona}
                            teamType={window.teamType}
                        >
                            {Component}
                        </PageWrapper>
                    </CrossFlowProvider>
                </SentryIntlProvider>
            );
        } else {
            hydrateRoot(
                // @ts-ignore
                document.getElementById("wac-root"),
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                >
                    <CrossFlowProvider locale={locale}>
                        <Layout
                            location={window.location}
                            pricing={window.prices}
                            availableProducts={window.availableProducts}
                            signupUserTraits={window.signupUserTraits}
                            agileArticle={window.agileArticle}
                            agileOverview={window.agileOverview}
                            tutorial={window.tutorial}
                            customersLanding={window.customersLanding}
                            templates={window.templates}
                            partnerTemplates={window.partnerTemplates}
                            collections={window.collections}
                            categories={window.categories}
                            userProfile={window.userProfile}
                            countryCode={window.countryCode}
                            themeStyles={window.themeStyles}
                            anchorContents={window.anchorContents}
                            persona={window.persona}
                            teamType={window.teamType}
                        >
                            {Component}
                        </Layout>
                    </CrossFlowProvider>
                </SentryIntlProvider>
            );
        }
    });
};
